(function (angular) {
	'use strict';

	// This port must always match the port name defined in `elm/shared/src/PlanHat/PlanHat.elm`
	const planHatTrackingPort = 'out_PlanHat_sendPlanHatTracking';

	const elmInit = (moduleName, elmModule, node, flags, elmVersion) => {
		switch (parseFloat(elmVersion)) {
			default:
				const app = flags
					? elmModule.init({ node: node, flags: { moduleName, ...flags } })
					: elmModule.init({ node }, { moduleName });
				registerLogger(app);
				registerRedirectToLoginPage(app);
				window.pmtUi.init(app);

				return app;
		}
	};

	/**
	 * Register logger for elm errors
	 *
	 * NOTICE: We use an indirect reference to window.PinMeToLogger because
	 * when the parent scope is initialized (the iife), it has not yet been registered
	 * on window.
	 *
	 * If we move the `pmt-logger.js` to load earlier the angular modules it refers
	 * to has not yet been loaded. When elmInit fires which calls this method we
	 * should be able to access PinMeToLogger indirectly.
	 *
	 * TODO: Slice and dice the build so we can inject the PinMeToLogger in the top scope.
	 *
	 * @param app Elm application
	 */
	function registerLogger(app) {
		if (app.ports && app.ports.pinMeToLoggerLog) {
			const logger = window.PinMeToLogger || console;
			app.ports.pinMeToLoggerLog.subscribe(logger.log);
		}
	}
	function registerRedirectToLoginPage(app) {
		const redirectFunc = () => {
			location.href = window.pmtEnv.urls.login + '?returnUrl=' + encodeURIComponent(location.href);
		};
		app.ports?.main_redirectToLoginPage?.subscribe(redirectFunc);
		app.ports?.posts_redirectToLoginPage?.subscribe(redirectFunc);
	}

	const elmService = function ($i18next, pmtPlanHatService, pmtEnvService) {
		const runningModules = {};

		function create(div, module, args, elmVersion) {
			const parts = module.split('.');
			const elmModule = parts.reduce(function (obj, p) {
				if (obj == null) {
					console.log('incorrect ELM module name', module);
				}

				return obj[p];
			}, Elm);

			return elmInit(module, elmModule, div, args, elmVersion);
		}

		function callPort(port, args) {
			try {
				port.send(args);
			} catch (err) {
				console.log('Failed to call port', { port: port, args: args });
				console.error(err);
			}
		}

		function assertPort(module, portName) {
			if (
				runningModules[module] &&
				runningModules[module].module &&
				runningModules[module].module.ports[portName] == null
			) {
				throw `Cannot find portName '${portName}' in module '${module}'`;
			}
		}

		const mapPortName = (prefix, id) => {
			if (id === pmtPlanHatService.elmPlanHatPortId) {
				return planHatTrackingPort;
			}

			return `${prefix}_${id}`;
		};

		const _planHatTrackingPortFunction = trackingItem => {
			pmtPlanHatService.track(trackingItem.action, trackingItem.info);
		};

		const _registerSubscription = (module, portName, portFunction) => {
			if (this.verifyPortExist(module, portName)) {
				assertPort(module, portName);
				runningModules[module].module.ports[portName].subscribe(portFunction);
				return true;
			}

			return false;
		};

		const _subscriptionFailedLog = (prefix, subscription, module, portName) => {
			if (!pmtEnvService.isDev) {
				return;
			}

			const availablePortsByPrefix = Object.keys(runningModules[module].module.ports).filter(port =>
				port.includes(prefix)
			);

			const availablePortsById = Object.keys(runningModules[module].module.ports).filter(port =>
				port.includes(subscription.id)
			);

			const error = availablePortsByPrefix.length
				? 'Something is likely wrong with the id of the port in Elm.'
				: 'Something is likely wrong with the prefix of the port in Elm.';

			console.error(`Failed to register subscription. ${error}`, {
				id: subscription.id,
				module: module,
				expectedPortName: portName,
				closeMatches: [...availablePortsById, ...availablePortsByPrefix]
			});
		};

		const _deregisterSubscription = (module, portName, portFunction) => {
			if (this.verifyPortExist(module, portName)) {
				assertPort(module, portName);
				runningModules[module].module.ports[portName].unsubscribe(portFunction);
			}
		};

		const registerSubscriptions = (module, subscriptions) => {
			const uniqueSubscriptionsPrefix = 'out_' + module.replace(/\./g, '_');
			if (subscriptions && typeof subscriptions.forEach === 'function') {
				subscriptions.forEach(s => {
					const portName = mapPortName(uniqueSubscriptionsPrefix, s.id);
					const isPortRegistered = _registerSubscription(module, portName, s.f);

					if (!isPortRegistered) {
						_subscriptionFailedLog(uniqueSubscriptionsPrefix, s, module, portName);
					}
				});
			}

			_registerSubscription(module, planHatTrackingPort, _planHatTrackingPortFunction);
		};

		const deregisterSubscriptions = (module, subscriptions) => {
			const uniqueSubscriptionsPrefix = 'out_' + module.replace(/\./g, '_');
			if (subscriptions && typeof subscriptions.forEach === 'function') {
				subscriptions.forEach(s => {
					const portName = mapPortName(uniqueSubscriptionsPrefix, s.id);
					_deregisterSubscription(module, portName, s.f);
				});
			}

			_deregisterSubscription(module, planHatTrackingPort, _planHatTrackingPortFunction);
		};

		function getTranslation(language) {
			if ($i18next && $i18next.i18n && $i18next.i18n.getDataByLanguage) {
				const lng = language || $i18next.i18n.language || 'en';
				const data = $i18next.i18n.getDataByLanguage(lng);
				if (!data) {
					return {};
				}

				function fixSingular(obj, v, k) {
					if (typeof v !== 'string') {
						Object.keys(v).forEach(x => fixSingular(v, v[x], x));
					} else if (k.endsWith('_0')) {
						obj[k.replace('_0', '')] = obj[k.replace('_0', '')] || v;
					}
				}

				const result = {};
				Object.keys(data).forEach(ns => {
					Object.assign(result, data[ns]);

					fixSingular(data, data[ns], ns);
				});
				return result;
			}
			return {};
		}

		this.getTranslation = getTranslation;

		this.sendData = function (module, port, data) {
			if (runningModules[module]) {
				const m = runningModules[module].module;
				if (m.ports[port] == null) {
					console.error(`Port '${port}' does not exist on module '${module}'`);
				}
				if (data && typeof data === 'object' && !Array.isArray(data)) {
					var d = {
						translations: getTranslation(),
						language: $i18next?.i18n?.language || 'en',
						...data
					};
					callPort(m.ports[port], d);
				} else {
					callPort(m.ports[port], data);
				}
			}
		};

		this.verifyPortExist = (module, portName) => {
			if (runningModules[module]) {
				const m = runningModules[module].module;

				return !!(m.ports && m.ports[portName]);
			}

			return false;
		};

		this.checkPorts = function (m, passArgsPort, module) {
			if (m.ports[passArgsPort] == null) {
				console.error(`Port '${passArgsPort}' does not exist on module '${module}'`);
			}
		};

		this.initialize = function ($element, module, args, subscriptions, elmVersion) {
			if (runningModules[module]) {
				const m = runningModules[module].module;
				const d = runningModules[module].div;
				$element.empty();
				$element.append(d);
				if (m.ports) {
					if (m.ports.enableSubscriptions) {
						m.ports.enableSubscriptions.send(null);
					}
					/* Ports are global.
						We have no way to destroy an created ELM app so some ELM apps need arguments to show different data (ex: Details.App, InsightLocks)
					*/
					const moduleUniquePortName = 'passArgs_' + module.replace(/\./g, '_');
					const passArgsPort = Object.getOwnPropertyNames(m.ports).find(
						x => x === moduleUniquePortName
					);
					if (passArgsPort && args) {
						this.checkPorts(m, passArgsPort, module);

						callPort(m.ports[passArgsPort], args);
					}
				}
				registerSubscriptions(module, subscriptions);

				if (m.ports.onInit) {
					this.sendData(module, 'onInit', { name: module, flags: args });
				}
			} else {
				// Elm replaces its node so we need wrapping div which is removed/appended
				// to the DOM later by this service on next call to initialize.
				// 1. Not wrapping would only work if Elm app has a div as root node
				// 2. This also fixes bug where the view was not rendered after angular-navigation
				const wrapper = $element.children()[0];
				const div = angular.element(wrapper).children()[0];

				runningModules[module] = {
					module: create(div, module, args, elmVersion),
					args: args,
					div: wrapper
				};
				registerSubscriptions(module, subscriptions);
			}
		};

		this.deactivate = function (module, subscriptions) {
			const mod = runningModules[module];
			if (mod) {
				const m = mod.module;

				if (m.ports && m.ports.disableSubscriptions) {
					m.ports.disableSubscriptions.send(null);
				}
				if (m.ports.onDestroy) {
					this.sendData(module, 'onDestroy', { name: module });
				}
			}

			deregisterSubscriptions(module, subscriptions);
		};
	};

	angular
		.module('PinMeTo.elm')
		.service('pmtElmService', ['$i18next', 'pmtPlanHatService', 'pmtEnvService', elmService]);
})(window.angular);
